import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Table,
  Spin,
  Alert,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Tag,
  Typography,
  Space,
  Drawer,
  Tabs
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  PlusOutlined,
  KeyOutlined,
  SettingOutlined,
  SafetyOutlined
} from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import PermissionMatrix from '../components/PermissionMatrix';
import PermissionSummary from '../components/PermissionSummary';
import RolePermissionManager from '../components/RolePermissionManager';

const { Title } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const UserManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [addModalVisible, setAddModalVisible] = useState(false); // State for Add modal
  const [editForm] = Form.useForm();
  const [addForm] = Form.useForm(); // Form for Add modal
  const [passwordForm] = Form.useForm(); // Form for Set Password modal
  const [submitLoading, setSubmitLoading] = useState(false); // Shared loading state for modals
  const [passwordModalVisible, setPasswordModalVisible] = useState(false); // State for Set Password modal
  const [passwordUserId, setPasswordUserId] = useState(null); // User ID for password change
  const [permissionDrawerVisible, setPermissionDrawerVisible] = useState(false); // State for Permission drawer
  const [selectedUserForPermissions, setSelectedUserForPermissions] = useState(null); // User selected for permission management
  const [availablePermissions, setAvailablePermissions] = useState({}); // Available permissions from backend
  const { user } = useAuth(); // Get user role for conditional rendering/checks

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Assuming the token is sent automatically via axios interceptors or headers
      const response = await axios.get('/users'); // Removed prefix
      setUsers(response.data.data.users);
    } catch (err) {
      console.error("Error fetching users:", err);
      setError(err.response?.data?.message || 'Failed to fetch users.');
      message.error(err.response?.data?.message || 'Failed to fetch users.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // --- Edit Modal Logic ---
  const showEditModal = (userRecord) => {
    setEditingUser(userRecord);
    editForm.setFieldsValue({
      name: userRecord.name,
      username: userRecord.username,
      email: userRecord.email,
      role: userRecord.role,
    });
    setEditModalVisible(true);
  };

  // --- Add Modal Logic ---
   const showAddModal = () => {
    addForm.resetFields();
    setAddModalVisible(true);
  };

  const handleAddCancel = () => {
    setAddModalVisible(false);
    addForm.resetFields();
  };

  const onFinish = async (values) => {
    setSubmitLoading(true);
    console.log('Form values being sent:', JSON.stringify(values, null, 2));
    try {
      const response = await axios.post('/users', values); // Removed prefix
      console.log('User creation response:', response.data);
      message.success('User created successfully!');
      handleAddCancel();
      fetchUsers(); // Refresh list
    } catch (err) {
      console.error("Error creating user:", err);
      console.error("Error response:", err.response?.data);
      // Check for specific error messages
      const errorMessage = err.response?.data?.message || 'Failed to create user.';

      if (errorMessage.includes('Email address is already registered')) {
        message.error('This email address is already in use. Please use a different email.');
      } else if (errorMessage.includes('Username is already taken')) {
        message.error('This username is already taken. Please choose a different username.');
      } else if (errorMessage.includes('Email or username is already registered')) {
        message.error('This email or username is already in use. Please use different credentials.');
      } else {
        // Show the actual error message from the server
        message.error(errorMessage);
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEditCancel = () => {
    setEditModalVisible(false);
    setEditingUser(null);
    editForm.resetFields();
  };

  const handleUpdateUser = async (values) => {
    if (!editingUser?._id) return;
    setSubmitLoading(true);
    try {
      // Send fields that are allowed to be updated
      const updatePayload = {
        name: values.name,
        username: values.username,
        email: values.email,
        role: values.role,
      };
      await axios.patch(`/users/${editingUser._id}`, updatePayload); // Removed prefix
      message.success('User updated successfully!');
      handleEditCancel();
      fetchUsers(); // Refresh list
    } catch (err) {
      console.error("Error updating user:", err);
      // Check for specific error messages
      const errorMessage = err.response?.data?.message || 'Failed to update user.';

      if (errorMessage.includes('Email address is already registered')) {
        message.error('This email address is already in use. Please use a different email.');
      } else if (errorMessage.includes('Username is already taken')) {
        message.error('This username is already taken. Please choose a different username.');
      } else if (errorMessage.includes('Email or username is already registered')) {
        message.error('This email or username is already in use. Please use different credentials.');
      } else {
        message.error(errorMessage);
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  // --- Delete Logic ---
  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`/users/${userId}`); // Removed prefix
      message.success('User deleted successfully!');
      fetchUsers(); // Refresh list
    } catch (err) {
      console.error("Error deleting user:", err);
      message.error(err.response?.data?.message || 'Failed to delete user.');
    }
  };

  // --- Set Password Modal Logic ---
  const showPasswordModal = (userId) => {
    setPasswordUserId(userId);
    passwordForm.resetFields();
    setPasswordModalVisible(true);
  };

  const handlePasswordCancel = () => {
    setPasswordModalVisible(false);
    setPasswordUserId(null);
    passwordForm.resetFields();
  };

  const handleSetPassword = async (values) => {
    if (!passwordUserId) return;
    setSubmitLoading(true);
    try {
      await axios.patch(`/users/${passwordUserId}/set-password`, { password: values.password }); // Removed prefix
      message.success('Password updated successfully!');
      handlePasswordCancel();
      // No need to fetchUsers() as password change doesn't affect the table display
    } catch (err) {
      console.error("Error setting password:", err);
      message.error(err.response?.data?.message || 'Failed to set password.');
    } finally {
      setSubmitLoading(false);
    }
  };

  // --- Permission Management Functions ---
  const showPermissionDrawer = (user) => {
    setSelectedUserForPermissions(user);
    setPermissionDrawerVisible(true);
  };

  const handlePermissionDrawerClose = () => {
    setPermissionDrawerVisible(false);
    setSelectedUserForPermissions(null);
  };

  const handlePermissionsChange = (newPermissions) => {
    // Update the user in the users list with new permissions
    setUsers(prevUsers =>
      prevUsers.map(user =>
        user._id === selectedUserForPermissions._id
          ? { ...user, permissions: newPermissions }
          : user
      )
    );
  };

  // Load available permissions on component mount
  useEffect(() => {
    const loadAvailablePermissions = async () => {
      try {
        // Corrected to use the new, correct endpoint
        const response = await axios.get('/permissions/all');
        setAvailablePermissions(response.data.data);
      } catch (err) {
        console.error('Error loading available permissions:', err);
        // Avoid showing a message for this as the RolePermissionManager will show one
      }
    };

    loadAvailablePermissions();
  }, []);

  // --- Table Columns ---
  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      render: (username) => <Tag color="green">{username}</Tag>,
      sorter: (a, b) => (a.username || '').localeCompare(b.username || ''),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
    {
      title: 'Created At', // Optional: Show creation date
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => date ? new Date(date).toLocaleDateString() : 'N/A',
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role) => {
        let color = 'default';
        if (role === 'Admin') color = 'volcano';
        else if (role === 'Provider') color = 'geekblue'; // Example role
        else if (role === 'Nurse') color = 'green'; // Example role
        return <Tag color={color}>{role}</Tag>;
      },
      filters: [
        { text: 'Admin', value: 'Admin' },
        { text: 'MD (Provider)', value: 'MD' },
        { text: 'Nursing', value: 'Nursing' },
        { text: 'Customer Service', value: 'Customer Service' },
        { text: 'Pharmacy', value: 'Pharmacy' },
        { text: 'Laboratory', value: 'Laboratory' },
        { text: 'Radiology', value: 'Radiology' },
        { text: 'Business Development', value: 'Business Development' }
      ],
      onFilter: (value, record) => record.role.includes(value),
    },
    {
      title: 'Permissions',
      key: 'permissions',
      render: (_, record) => (
        <PermissionSummary
          username={record.username}
          userRole={record.role}
          permissions={record.permissions || {}}
          availablePermissions={availablePermissions}
          compact={true}
        />
      ),
      width: 200,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => showEditModal(record)}
            style={{ marginRight: 8 }}
          >
            Edit
          </Button>
          <Button
            type="link"
            icon={<KeyOutlined />}
            onClick={() => showPasswordModal(record._id)}
            style={{ marginRight: 8 }}
          >
            Set Password
          </Button>
          <Button
            type="link"
            icon={<SafetyOutlined />}
            onClick={() => showPermissionDrawer(record)}
            style={{ marginRight: 8 }}
          >
            Permissions
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this user?"
            onConfirm={() => handleDeleteUser(record._id)}
            okText="Yes"
            cancelText="No"
            // Disable delete for the logged-in user? Or based on some logic?
            // disabled={record._id === user?._id}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  // Check if user has Admin role to access this page
  if (user?.role !== 'Admin') {
    return (
      <div style={{ padding: '20px' }}>
        <Alert 
          message="Access Denied" 
          description="You do not have permission to access User Management. Only Admin users can view this page." 
          type="error" 
          showIcon 
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <Title level={2} style={{ margin: 0, marginBottom: 24 }}><SettingOutlined /> User & Role Management</Title>
      <Tabs defaultActiveKey="1">
        <TabPane
          tab={
            <span>
              <UserOutlined />
              User Accounts
            </span>
          }
          key="1"
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <Title level={4} style={{ margin: 0 }}>Manage Individual User Accounts</Title>
            <Button type="primary" icon={<PlusOutlined />} onClick={showAddModal}>
              Add User
            </Button>
          </div>
          {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
          <Spin spinning={loading}>
            <Table
              columns={columns}
              dataSource={users}
              rowKey="_id"
              pagination={{ pageSize: 10 }}
              style={{ marginTop: 16 }}
            />
          </Spin>
        </TabPane>
        <TabPane
          tab={
            <span>
              <SafetyOutlined />
              Default Role Permissions
            </span>
          }
          key="2"
        >
          <RolePermissionManager />
        </TabPane>
      </Tabs>

      {/* Add User Modal */}
      <Modal
        title="Add New User"
        visible={addModalVisible}
        onCancel={handleAddCancel}
        footer={null}
        destroyOnClose
      >
        <Spin spinning={submitLoading}>
          <Form form={addForm} layout="vertical" onFinish={onFinish}>
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: 'Please input the user name!' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="username"
              label="Username"
              rules={[
                { required: true, message: 'Please input a username!' },
                { min: 3, message: 'Username must be at least 3 characters long!' },
                { max: 30, message: 'Username must be less than 30 characters long!' },
                { pattern: /^[a-zA-Z0-9._-]+$/, message: 'Username can only contain letters, numbers, dots, underscores, and hyphens!' }
              ]}
            >
              <Input placeholder="Enter username (3-30 characters)" />
            </Form.Item>
            <Form.Item
              name="email"
              label="Email"
              rules={[{ required: true, type: 'email', message: 'Please input a valid email!' }]}
            >
              <Input />
            </Form.Item>
             <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: 'Please input a password!' },
                { min: 8, message: 'Password must be at least 8 characters long!' }
              ]}
            >
              <Input.Password />
            </Form.Item>
            <Form.Item
              name="role"
              label="Role"
              rules={[{ required: true, message: 'Please select a role!' }]}
            >
              <Select placeholder="Select a role">
                {/* Match roles defined in backend/models/User.js */}
                <Option value="Admin">Admin</Option>
                <Option value="MD">MD (Provider)</Option>
                <Option value="Nursing">Nursing</Option>
                <Option value="Customer Service">Customer Service</Option>
                <Option value="Pharmacy">Pharmacy</Option>
                <Option value="Laboratory">Laboratory</Option>
                <Option value="Radiology">Radiology</Option>
                <Option value="Business Development">Business Development</Option>
              </Select>
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={submitLoading}>
                Create User
              </Button>
              <Button style={{ marginLeft: 8 }} onClick={handleAddCancel}>
                Cancel
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        title="Edit User"
        visible={editModalVisible}
        onCancel={handleEditCancel}
        footer={null}
        destroyOnClose
      >
        <Spin spinning={submitLoading}>
          <Form form={editForm} layout="vertical" onFinish={handleUpdateUser}>
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: 'Please input the user name!' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="username"
              label="Username"
              rules={[
                { required: true, message: 'Please input a username!' },
                { min: 3, message: 'Username must be at least 3 characters long!' },
                { max: 30, message: 'Username must be less than 30 characters long!' },
                { pattern: /^[a-zA-Z0-9._-]+$/, message: 'Username can only contain letters, numbers, dots, underscores, and hyphens!' }
              ]}
            >
              <Input placeholder="Enter username (3-30 characters)" />
            </Form.Item>
            <Form.Item
              name="email"
              label="Email"
              rules={[{ required: true, type: 'email', message: 'Please input a valid email!' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="role"
              label="Role"
              rules={[{ required: true, message: 'Please select a role!' }]}
            >
              <Select placeholder="Select a role">
                {/* Match roles defined in backend/models/User.js */}
                <Option value="Admin">Admin</Option>
                <Option value="MD">MD (Provider)</Option>
                <Option value="Nursing">Nursing</Option>
                <Option value="Customer Service">Customer Service</Option>
                <Option value="Pharmacy">Pharmacy</Option>
                <Option value="Laboratory">Laboratory</Option>
                <Option value="Radiology">Radiology</Option>
                <Option value="Business Development">Business Development</Option>
              </Select>
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={submitLoading}>
                Save Changes
              </Button>
              <Button style={{ marginLeft: 8 }} onClick={handleEditCancel}>
                Cancel
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>

      {/* Set Password Modal */}
      <Modal
        title="Set New Password"
        visible={passwordModalVisible}
        onCancel={handlePasswordCancel}
        footer={null}
        destroyOnClose
      >
        <Spin spinning={submitLoading}>
          <Form form={passwordForm} layout="vertical" onFinish={handleSetPassword}>
            <Form.Item
              name="password"
              label="New Password"
              rules={[
                { required: true, message: 'Please input the new password!' },
                { min: 8, message: 'Password must be at least 8 characters long!' }
              ]}
              hasFeedback // Adds feedback icons based on validation status
            >
              <Input.Password placeholder="Enter new password" />
            </Form.Item>
             <Form.Item
              name="passwordConfirm"
              label="Confirm New Password"
              dependencies={['password']} // Depends on the password field
              hasFeedback
              rules={[
                { required: true, message: 'Please confirm the new password!' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('The two passwords that you entered do not match!'));
                  },
                }),
              ]}
            >
              <Input.Password placeholder="Confirm new password" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={submitLoading}>
                Set Password
              </Button>
              <Button style={{ marginLeft: 8 }} onClick={handlePasswordCancel}>
                Cancel
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>

      {/* Permission Management Drawer */}
      <Drawer
        title={
          <Space>
            <SafetyOutlined />
            <span>User Permissions</span>
            {selectedUserForPermissions && (
              <Tag color="blue">{selectedUserForPermissions.username}</Tag>
            )}
          </Space>
        }
        width={800}
        open={permissionDrawerVisible}
        onClose={handlePermissionDrawerClose}
        destroyOnClose
      >
        {selectedUserForPermissions && (
          <Tabs defaultActiveKey="matrix">
            <TabPane tab="Permission Matrix" key="matrix">
              <PermissionMatrix
                userId={selectedUserForPermissions._id}
                username={selectedUserForPermissions.username}
                userRole={selectedUserForPermissions.role}
                onPermissionsChange={handlePermissionsChange}
              />
            </TabPane>
            <TabPane tab="Permission Summary" key="summary">
              <PermissionSummary
                username={selectedUserForPermissions.username}
                userRole={selectedUserForPermissions.role}
                permissions={selectedUserForPermissions.permissions || {}}
                availablePermissions={availablePermissions}
              />
            </TabPane>
          </Tabs>
        )}
      </Drawer>

    </div>
  );
};

export default UserManagementPage;
